import { GoogleGenerativeAI } from "@google/generative-ai";

export interface FAQItem {
  question: string;
  question_en: string;
  answer: string;
  answer_en: string;
  keywords: string[];
  embedding: number[];
}

export interface SearchResult {
  item: FAQItem;
  similarity: number;
}

class VectorFAQService {
  private genAI: GoogleGenerativeAI;
  private faqItems: FAQItem[] = [];
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private initError: Error | null = null;

  constructor(apiKey: string) {
    this.genAI = new GoogleGenerativeAI(apiKey);
    console.log('🔧 [Vector FAQ] Service created');
    // Start initialization immediately
    this.initPromise = this.doInitialize();
  }

  /**
   * Calculate cosine similarity between two vectors
   * Returns a value between 0 and 1 (1 = identical, 0 = completely different)
   */
  private cosineSimilarity(vecA: number[], vecB: number[]): number {
    if (!vecA || !vecB || vecA.length !== vecB.length) {
      console.warn('⚠️ [Vector FAQ] Invalid vectors for similarity calculation', {
        vecALength: vecA?.length,
        vecBLength: vecB?.length,
      });
      return 0;
    }

    let dotProduct = 0;
    let normA = 0;
    let normB = 0;

    for (let i = 0; i < vecA.length; i++) {
      dotProduct += vecA[i] * vecB[i];
      normA += vecA[i] * vecA[i];
      normB += vecB[i] * vecB[i];
    }

    const denominator = Math.sqrt(normA) * Math.sqrt(normB);
    return denominator === 0 ? 0 : dotProduct / denominator;
  }

  /**
   * Generate embedding for text using Google's embedding model
   */
  private async generateEmbedding(text: string): Promise<number[]> {
    console.log(`🔄 [Vector FAQ] Generating embedding for: "${text.substring(0, 50)}..."`);
    try {
      const model = this.genAI.getGenerativeModel({ 
        model: "gemini-embedding-001" 
      });
      // Use the same method as in generateEmbeddings.js script
      const result = await model.embedContent(text);
      const embedding = Array.from(result.embedding.values) as number[];
      console.log(`✅ [Vector FAQ] Embedding generated, length: ${embedding.length}`);
      return embedding;
    } catch (error) {
      console.error('❌ [Vector FAQ] Error generating embedding:', error);
      throw new Error(`Failed to generate embedding: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Internal initialization logic
   */
  private async doInitialize(): Promise<void> {
    console.log('🚀 [Vector FAQ] Starting initialization...');
    try {
      // Load pre-computed embedded FAQ data
      const response = await fetch('/questionAndAnswers_embedded.json');
      
      if (!response.ok) {
        throw new Error(`Failed to load FAQ data: ${response.status} ${response.statusText}`);
      }

      const faqData: FAQItem[] = await response.json();
      console.log(`📦 [Vector FAQ] Loaded ${faqData.length} FAQ items from file`);

      // Filter out items without embeddings
      this.faqItems = faqData.filter(item => 
        item.embedding && Array.isArray(item.embedding) && item.embedding.length > 0
      );

      if (this.faqItems.length === 0) {
        throw new Error('No FAQ items with valid embeddings found');
      }

      this.isInitialized = true;
      this.initError = null;
      console.log(`✅ [Vector FAQ] Successfully initialized with ${this.faqItems.length} embedded FAQs`);
      
      // Log sample FAQ for verification
      if (this.faqItems[0]) {
        console.log(`📋 [Vector FAQ] Sample FAQ: "${this.faqItems[0].question.substring(0, 50)}..."`);
      }
    } catch (error) {
      console.error('❌ [Vector FAQ] Initialization failed:', error);
      this.initError = error instanceof Error ? error : new Error('Unknown initialization error');
      this.isInitialized = false;
    }
  }

  /**
   * Load pre-computed embeddings from JSON file
   */
  async initialize(): Promise<void> {
    // If already initialized, return immediately
    if (this.isInitialized) {
      console.log('ℹ️ [Vector FAQ] Already initialized');
      return;
    }

    // If initialization is in progress, wait for it
    if (this.initPromise) {
      console.log('⏳ [Vector FAQ] Waiting for initialization to complete...');
      await this.initPromise;
      
      // Check if there was an error
      if (this.initError) {
        console.warn('⚠️ [Vector FAQ] Previous initialization failed, retrying...');
        this.initPromise = this.doInitialize();
        await this.initPromise;
      }
    }
  }

  /**
   * Search FAQs using semantic similarity
   * @param query - The search query
   * @param language - Language preference ('ar' or 'en')
   * @param topK - Number of results to return
   * @param minSimilarity - Minimum similarity threshold (0-1)
   */
  async search(
    query: string,
    language: 'ar' | 'en' = 'ar',
    topK: number = 3,
    minSimilarity: number = 0.5
  ): Promise<SearchResult[]> {
    console.log(`🔍 [Vector FAQ] ===== SEARCH START =====`);
    console.log(`🔍 [Vector FAQ] Query: "${query}"`);
    console.log(`🔍 [Vector FAQ] Language: ${language}, TopK: ${topK}, MinSimilarity: ${minSimilarity}`);
    console.log(`🔍 [Vector FAQ] Initialized: ${this.isInitialized}, FAQs count: ${this.faqItems.length}`);
    
    // Ensure initialization
    if (!this.isInitialized) {
      console.log(`⏳ [Vector FAQ] Not initialized, calling initialize()...`);
      await this.initialize();
    }

    // Check if initialization succeeded
    if (!this.isInitialized) {
      console.error('❌ [Vector FAQ] Service failed to initialize');
      if (this.initError) {
        throw this.initError;
      }
      throw new Error('FAQ service failed to initialize');
    }

    if (this.faqItems.length === 0) {
      console.warn('⚠️ [Vector FAQ] No FAQs available after initialization');
      return [];
    }

    if (!query || query.trim().length === 0) {
      console.warn('⚠️ [Vector FAQ] Empty query provided');
      return [];
    }

    console.log(`🔍 [Vector FAQ] Searching ${this.faqItems.length} FAQs for: "${query}"`);

    try {
      // Generate embedding for the query
      console.log(`🔄 [Vector FAQ] Generating query embedding...`);
      const queryEmbedding = await this.generateEmbedding(query);
      console.log(`✅ [Vector FAQ] Query embedding generated, length: ${queryEmbedding.length}`);

      // Calculate similarity with all FAQ items
      const results: SearchResult[] = [];
      const allSimilarities: { question: string; similarity: number }[] = [];

      for (const item of this.faqItems) {
        if (!item.embedding || item.embedding.length === 0) {
          console.warn(`⚠️ [Vector FAQ] Skipping item without embedding: ${item.question.substring(0, 30)}...`);
          continue;
        }

        const similarity = this.cosineSimilarity(queryEmbedding, item.embedding);
        
        // Log all similarities for debugging
        allSimilarities.push({
          question: item.question.substring(0, 50),
          similarity: similarity
        });
        
        // Only include results above minimum similarity threshold
        if (similarity >= minSimilarity) {
          results.push({
            item,
            similarity,
          });
        }
      }

      // Log all similarities sorted
      allSimilarities.sort((a, b) => b.similarity - a.similarity);
      console.log(`📊 [Vector FAQ] Similarity scores (top 5 of ${allSimilarities.length}):`);
      allSimilarities.slice(0, 5).forEach((sim, i) => {
        console.log(`  ${i + 1}. [${(sim.similarity * 100).toFixed(1)}%] ${sim.question}...`);
      });

      // Sort by similarity (highest first) and return top K
      results.sort((a, b) => b.similarity - a.similarity);
      const topResults = results.slice(0, topK);
      
      console.log(`✅ [Vector FAQ] Found ${topResults.length} results above threshold ${minSimilarity}:`);
      topResults.forEach((result, i) => {
        const question = language === 'ar' ? result.item.question : result.item.question_en;
        const answer = language === 'ar' ? result.item.answer : result.item.answer_en;
        console.log(`  ${i + 1}. [${(result.similarity * 100).toFixed(1)}%] Q: ${question.substring(0, 60)}...`);
        console.log(`     A: ${answer.substring(0, 80)}...`);
      });

      console.log(`🔍 [Vector FAQ] ===== SEARCH END =====`);
      return topResults;
    } catch (error) {
      console.error('❌ [Vector FAQ] Search failed:', error);
      console.error('❌ [Vector FAQ] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown',
        stack: error instanceof Error ? error.stack : 'No stack',
      });
      throw error;
    }
  }

  /**
   * Get current date/time string for AI context (so events are described as past/future correctly)
   */
  private getCurrentDateTimeContext(language: 'ar' | 'en'): string {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
    const timeStr = now.toTimeString().slice(0, 5);  // HH:mm
    const dayName = language === 'ar'
      ? now.toLocaleDateString('ar-EG', { weekday: 'long' })
      : now.toLocaleDateString('en-US', { weekday: 'long' });
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    if (language === 'ar') {
      return `[التاريخ والوقت الحاليان (للمرجع): ${dateStr}، ${dayName}، الساعة ${timeStr} (${tz}). عند ذكر أي أحداث أو تواريخ في المعرفة: إذا كانت قبل هذا التاريخ فقد حدثت بالفعل—اصرح عنها في الماضي ولا تقل إنها ستحدث في المستقبل.]`;
    }
    return `[Current date and time (for reference): ${dateStr}, ${dayName}, ${timeStr} (${tz}). When the knowledge mentions events or dates: if they are before this date, they have already happened—describe them in the past, not as future.]`;
  }

  /**
   * Format search results for AI consumption
   */
  formatResultsForAI(results: SearchResult[], language: 'ar' | 'en' = 'ar'): string {
    if (results.length === 0) {
      return language === 'ar' 
        ? '[لم يتم العثور على نتائج ذات صلة في قاعدة المعرفة]'
        : '[No relevant results found in knowledge base]';
    }

    const dateTimeContext = this.getCurrentDateTimeContext(language);
    const header = language === 'ar'
      ? `[نتائج البحث من قاعدة المعرفة - استخدم هذه الإجابات الدقيقة للإجابة على سؤال المستخدم. لا تخترع معلومات. استخدم الإجابات كما هي من قاعدة المعرفة.]\n\n${dateTimeContext}`
      : `[Search results from knowledge base - use these exact answers to respond to the user question. Do not invent information. Use the answers as they are from the knowledge base.]\n\n${dateTimeContext}`;

    const formattedResults = results.map((result, index) => {
      const question = language === 'ar' ? result.item.question : result.item.question_en;
      const answer = language === 'ar' ? result.item.answer : result.item.answer_en;
      const confidence = (result.similarity * 100).toFixed(1);
      
      if (language === 'ar') {
        return `[نتيجة ${index + 1} - دقة: ${confidence}%]

السؤال: ${question}

الإجابة الدقيقة: ${answer}

---
`;
      } else {
        return `[Result ${index + 1} - confidence: ${confidence}%]

Question: ${question}

Exact Answer: ${answer}

---
`;
      }
    }).join('\n');

    const footer = language === 'ar'
      ? '\n[مهم: استخدم الإجابات أعلاه للإجابة على سؤال المستخدم. إذا كانت النتيجة الأولى تطابق السؤال، استخدم إجابتها مباشرة. لا تخترع معلومات إضافية. أي حدث أو تاريخ مذكور في المعرفة وكان قبل التاريخ الحالي المذكور أعلاه فقد حدث بالفعل—اذكره في الماضي (مثل "كان"، "تم"، "انتهى") وليس كمستقبل.]'
      : '\n[Important: Use the answers above to respond to the user question. If the first result matches the question, use its answer directly. Do not invent additional information. Any event or date in the knowledge that is before the current date above has already happened—refer to it in the past tense (e.g. "was", "took place", "ended"), not as future.]';

    return `${header}\n\n${formattedResults}${footer}`;
  }

  /**
   * Get statistics about loaded FAQ data
   */
  getStats(): { total: number } {
    return {
      total: this.faqItems.length,
    };
  }
}

// Create singleton instance
const API_KEY =  "AIzaSyCc0KGMh0lBOorfrxY74UR44_FyAldKliE" ;
export const vectorFAQService = new VectorFAQService(API_KEY);

